/****************************************************
 * Module Name:  StonePotential.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import { Box, Drawer, Tooltip, Button, Typography, TextField } from "@mui/material";
import React, { useState, useEffect } from "react";
import StoneProxy from "../../utility/proxy/manager/stoneProxy";
import CommonSnackbar from "../common/commonSnackBar";
import Constants from "../../utility/constants";
import StockSearchCriteria from "../operations/stockSearchCriteria";
import TuneIcon from '@mui/icons-material/Tune';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import StoneDetailModal from "../common/stoneDetailModal";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import VideoLibraryTwoToneIcon from '@mui/icons-material/VideoLibraryTwoTone';
import StoneMediaModal from "../common/stoneMediaModal";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import NoPermission from "../common/noPermission";
import WolzaIndiaExportProxy from "../../utility/proxy/nivodautility/wolzaIndiaExportProxy";
import CriteriaInvoiceDetail from "./criteriaInvoiceDetailModal";
import ConsigneeDetailModal from "./consigneeDetailModal";
import CustomerDetailModal from "./customerDetailModal";
import MarketSellWolzaModal from "../common/marketSellWolzaModal";
import { Modal } from "react-bootstrap";
import logo from '../../images/pansuriya.jpg'
import PageTitle from "../common/pageTitle";

const StonePotential = () => {

    const [searchCriteria, setSearchCriteria] = useState("");
    const [stockCriteriaState, setStockCriteriaState] = useState('');
    const [stoneListLoader, setStoneListLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [stoneList, setStoneList] = useState([]);
    const [stonePotentialList, setStonePotentialList] = useState([]);
    const [drawerExpanded, setDrawerExpanded] = useState(true);
    const [fromDate, setFromDate] = useState(dayjs().subtract(2, 'month').startOf('month').toDate());
    const [toDate, setToDate] = useState(dayjs().toDate());
    const [stoneId, setStoneId] = useState("");
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [showInvoiceDetailModal, setShowInvoiceDetailModal] = useState(false);
    const [showCustomerDetailModal, setShowCustomerDetailModal] = useState(false);
    const [showConsigneeDetailModal, setShowConsigneeDetailModal] = useState(false);
    const [showWolzaDetailModal, setShowWolzaDetailModal] = useState(false);
    const [stoneMediaURL, setStoneMediaURL] = useState([]);
    const [showStoneMediaModal, setShowStoneMediaModal] = useState(false);
    const [stoneData, setStoneData] = useState({});
    const [criteriaData, setCriteriaData] = useState([]);
    const [access, setAccess] = useState(false);
    const [stonetype, setStonetype] = useState("");

    useEffect(() => {
        PageTitle("OPERATION_STONEPOTENTIAL");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.OPERATION_STONEPOTENTIAL
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        }
    }

    const fetchLiveStoneData = (data, stockCriteriaStored) => {
        setSearchCriteria(data);
        setStockCriteriaState(stockCriteriaStored);
        setStoneListLoader(true);

        data.status = [];
        data.status.push('AVAILABLE');
        data.status.push('HOLD');
        data.status.push('MEMO_OUT');

        StoneProxy.GetFilteredStock(
            data, (responseData) => {
                if (responseData) {
                    setStoneList(responseData);
                    setStoneListLoader(false);
                    if (responseData.length > 0) {
                        handleSearch(responseData);
                    } else {
                        setStonePotentialList([]);
                    }
                } else {
                    setErrorSnackbar(true);
                    setStoneListLoader(false);
                }
            }
        );
    };

    const handleClose = () => {
        setShowStoneDetailModal(false);
        setShowConsigneeDetailModal(false);
        setShowCustomerDetailModal(false);
        setShowInvoiceDetailModal(false);
    };

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };


    const handleWolzaDetail = (stoneid) => {
        let data = {};
        const matchedRow = stoneList.find(data => data.stoneid === stoneid)
        if (matchedRow) {
            data = {
                stoneid: matchedRow.stoneid ? matchedRow.stoneid : null,
                cut: matchedRow.cut ? matchedRow.cut : null,
                pol: matchedRow.pol ? matchedRow.pol : null,
                sym: matchedRow.sym ? matchedRow.sym : null,
                flo: matchedRow.flo ? matchedRow.flo : null,
                shape: matchedRow.shape ? matchedRow.shape : null,
                clarity: matchedRow.clarity ? matchedRow.clarity : null,
                color: matchedRow.color ? matchedRow.color : null,
                size: matchedRow.sizemasterid ? matchedRow.sizemasterid : null,
                stonetype: matchedRow.stonetype && matchedRow.stonetype === "N" ? "N" : "LG",
                lab: matchedRow.lab ? matchedRow.lab : null,
            }
        }
        setStoneData(data);
        setShowWolzaDetailModal(true);
    }

    const handleConsigneeDetailModal = (stoneid) => {
        setStoneId(stoneid);
        setShowConsigneeDetailModal(true);

        const matchedRow = stoneList.find(data => data.stoneid === stoneid)
        if (matchedRow) {
            setStonetype(matchedRow.stonetype && matchedRow.stonetype === "N" ? "N" : "LG");
        }

    };

    const handleCustomerDetailModal = (stoneid) => {
        setStoneId(stoneid);
        setShowCustomerDetailModal(true);
    }

    const handleCustomerInvoiceDetailModal = (stoneid) => {
        let data = {};
        const matchedRow = stoneList.find(data => data.stoneid === stoneid)
        if (matchedRow) {
            data = {
                cut: matchedRow.cut ? [matchedRow.cut] : [],
                pol: matchedRow.pol ? [matchedRow.pol] : [],
                sym: matchedRow.sym ? [matchedRow.sym] : [],
                flo: matchedRow.flo ? [matchedRow.flo] : [],
                cutpolsym: matchedRow.cutpolsym ? [matchedRow.cutpolsym] : [],
                shape: matchedRow.shape ? [matchedRow.shape] : [],
                clarity: matchedRow.clarity ? [matchedRow.clarity] : [],
                color: matchedRow.color ? [matchedRow.color] : [],
                size: matchedRow.sizemasterid ? [matchedRow.sizemasterid] : [],
                from: (fromDate !== null && fromDate !== "") ?
                    dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000Z" : null,
                to: (toDate !== null && toDate !== "") ?
                    dayjs(toDate).format('YYYY-MM-DD') + "T23:59:59.999Z" : null,
                lab: matchedRow.lab ? [matchedRow.lab] : [],
                stonetype: matchedRow.stonetype ? [matchedRow.stonetype] : [],
            }
        }
        setCriteriaData(data);
        setShowInvoiceDetailModal(true);
    };

    const handleSearch = (stoneList) => {
        setStoneListLoader(true);
        const stoneIdsList = stoneList.map(stone => stone.stoneid);

        let reqData = {
            stoneids: stoneIdsList,
            from: (fromDate !== null && fromDate !== "") ?
                dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000Z" : null,
            to: (toDate !== null && toDate !== "") ?
                dayjs(toDate).format('YYYY-MM-DD') + "T23:59:59.999Z" : null,
        }

        WolzaIndiaExportProxy.GetStonePotential(
            reqData,
            (responseData) => {
                if (responseData) {
                    let dataMap = new Map();
                    responseData.forEach(iData => {
                        dataMap.set(iData.stoneid, iData);
                    })
                    processStonePotentialData(dataMap, stoneList);
                }
                else {
                    setErrorSnackbar(true);
                    setStoneListLoader(false);
                }
            });
    };

    const processStonePotentialData = (responseDataMap, stoneList) => {

        let resultData = [];
        stoneList.forEach(iData => {

            iData = {
                ...iData,
                totalsellcount: 0,
                uniqueconsignees: 0,
                totalcustsellcount: 0,
                uniquecustomers: 0,
            }
            let obj = responseDataMap.get(iData.stoneid);
            if (obj) {
                iData.totalsellcount = obj.marketsellcount;
                iData.uniqueconsignees = obj.marketconsigneecount;
                iData.totalcustsellcount = obj.actualsellcount;
                iData.uniquecustomers = obj.actualcustomercount;
            }
            resultData.push(iData);
        })
        setStonePotentialList(resultData)
        setStoneListLoader(false);
    }

    const initialColumns = () => [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 80,
            renderCell: renderCellExpand,
        },
        {
            field: "stoneid",
            headerName: "STONEID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 80,
            renderCell: (params) => (Utility.NeedToShowStoneDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: ["AVAILABLE", "HOLD", "MEMO_OUT"],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.lab.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)

        },
        {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.cut.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.pol.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.sym.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.flo.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.shape.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "color",
            headerName: "COL",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.color.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "clarity",
            headerName: "CLR",
            headerAlign: "center",
            align: "center",
            width: 55,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.clarity.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "measurements",
            headerName: "MEASUREMENTS",
            headerAlign: "center",
            align: "center",
            width: 130,
            renderCell: renderCellExpand,
        },
        {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 100,
            renderCell: renderCellExpand,
        },
        {
            field: "tinge",
            headerName: "TINGE",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.tinge.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 80,
        },
        {
            field: "milky",
            headerName: "MILKY",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.milky.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 80,
        },
        {
            field: "age",
            headerName: "AGE",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => {
                if (params.value)
                    return Utility.ConvertDateToAge(params.value);
                else
                    return null;
            },
            renderCell: (params) => {
                return params.value;
            },
        },
        {
            field: "lastrevise",
            headerName: "LASTREVISE",
            headerAlign: "center",
            align: "center",
            width: 90,
            valueGetter: (params) => {
                if (params.value)
                    return Utility.ConvertDateToAge(params.value);
                else
                    return null;
            },
            renderCell: (params) => {
                return params.value;
            },
        },
        {
            field: "totalsellcount",
            headerName: "MKT SELL COUNT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 120,
            valueGetter: (params) => params.value !== undefined && params.value !== null ? params.value : 0,
            renderCell: (params) =>
                params.value > 0 ? (
                    <Button
                        sx={{
                            textDecorationLine: "underline",
                            color: 'black',
                        }}
                        onClick={() => handleWolzaDetail(params.row.stoneid)}
                    >
                        {params.value}
                    </Button>
                ) : (
                    <span>{params.value}</span>
                ),
            cellClassName: (params) => {
                if (params.value > 0)
                    return 'greenCell'
            },
        },
        {
            field: "uniqueconsignees",
            headerName: "MKT CONSIGNEES",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 130,
            renderCell: (params) =>
                params.value > 0 ? (
                    <Button
                        sx={{
                            textDecorationLine: "underline",
                            color: 'black',
                        }}
                        onClick={() => handleConsigneeDetailModal(params.row.stoneid)}
                    >
                        {params.value}
                    </Button>
                ) : (
                    <span>{params.value}</span>
                ),
            cellClassName: (params) => {
                if (params.value > 0)
                    return 'greenCell'
            },
        },
        {
            field: "totalcustsellcount",
            headerName: "TOTAL SELL COUNT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 150,
            valueGetter: (params) => params.value !== undefined && params.value !== null ? params.value : 0,
            renderCell: (params) =>
                params.value > 0 ? (
                    <Button
                        sx={{
                            textDecorationLine: "underline",
                            color: 'black',
                        }}
                        onClick={() => handleCustomerInvoiceDetailModal(params.row.stoneid)}
                    >
                        {params.value}
                    </Button>
                ) : (
                    <span>{params.value}</span>
                ),
            cellClassName: (params) => {
                if (params.value > 0)
                    return 'greenCell'
            },
        },
        {
            field: "uniquecustomers",
            headerName: "UNIQUE CUSTOMERS",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 150,
            renderCell: (params) =>
                params.value > 0 ? (
                    <Button
                        sx={{
                            textDecorationLine: "underline",
                            color: 'black',
                        }}
                        onClick={() => handleCustomerDetailModal(params.row.stoneid)}
                    >
                        {params.value}
                    </Button>
                ) : (
                    <span>{params.value}</span>
                ),
            cellClassName: (params) => {
                if (params.value > 0)
                    return 'greenCell'
            },
        },
        {
            field: "media",
            headerName: "MEDIA",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: (params) => (
                <Tooltip title={"Stone Media"} sx={{ cursor: "pointer" }}>
                    <VideoLibraryTwoToneIcon
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                            setStoneMediaURL(params.row.stonemediaurl);
                            setShowStoneMediaModal(true);
                        }} />
                </Tooltip>
            ),
        },
    ]

    const columns = initialColumns();
    function CustomToolbar() {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
                {searchCriteria === "" ? (
                    <Box />
                ) : stoneListLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() => fetchLiveStoneData(searchCriteria, stockCriteriaState)}
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }
    return (
        <Box>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            <Drawer
                anchor="left"
                open={drawerExpanded}
                style={{ width: "50%" }}
                onClose={() => setDrawerExpanded(!drawerExpanded)}>
                <StockSearchCriteria
                    displaySearchCriteria={false}
                    stockCriteriaStored={stockCriteriaState}
                    handleSearch={fetchLiveStoneData}
                    showSaveSearchAs={false}
                    setCustomerId={(data) => { }}
                    drawerClose={() => setDrawerExpanded(false)}
                    setFilters={(stockCriteriaStored) => setStockCriteriaState(stockCriteriaStored)}
                    readOnly={false}
                    enableReportNoSearchBox={false}
                />
            </Drawer>
            {showStoneDetailModal ? (
                <StoneDetailModal
                    show={showStoneDetailModal}
                    stoneId={stoneId}
                    handleClose={handleClose}
                />
            ) : null}
            {showWolzaDetailModal ? (
                <Modal
                    enforceFocus={false}
                    show={showWolzaDetailModal}
                    onHide={() => setShowWolzaDetailModal(false)}
                    dialogClassName="max-modalSize"
                    style={{ zIndex: 1050 }}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            &nbsp;&nbsp;
                            Wolza Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <MarketSellWolzaModal
                            handleClose={() => setShowWolzaDetailModal(false)}
                            stone={stoneData}
                            loadedCustomers={null}
                            fromDate={fromDate}
                            toDate={toDate}
                            mappingkey={null}
                        />
                    </Modal.Body>
                </Modal>
            ) : null}
            {showConsigneeDetailModal ? (
                <ConsigneeDetailModal
                    show={showConsigneeDetailModal}
                    handleClose={handleClose}
                    stoneid={stoneId}
                    fromDate={fromDate}
                    toDate={toDate}
                    stonetype={stonetype}
                />
            ) : null}

            {showCustomerDetailModal ? (
                <CustomerDetailModal
                    show={showCustomerDetailModal}
                    stoneid={stoneId}
                    handleClose={handleClose}
                    fromDate={fromDate}
                    toDate={toDate}
                />
            ) : null}
            {showStoneMediaModal ? (
                <>
                    <StoneMediaModal
                        show={showStoneMediaModal}
                        stoneMediaURL={stoneMediaURL}
                        handleClose={() => setShowStoneMediaModal(false)}
                    />
                </>
            ) : null}
            {showInvoiceDetailModal ? (
                <>
                    <CriteriaInvoiceDetail
                        show={showInvoiceDetailModal}
                        criteriaData={criteriaData}
                        handleClose={() => setShowInvoiceDetailModal(false)}
                    />
                </>
            ) : null}
            {
                access ? <NoPermission /> : (
                    <Box px={1} >
                        <Box
                            m="16px"
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "20px",
                                width: "100%", // full width so it adjusts inside parent
                                justifyContent: "flex-start",
                                alignItems: "flex-start",
                            }}
                        >
                            {/* All Filters Button */}
                            <Tooltip title="Show All Filters">
                                <Button
                                    variant="contained"
                                    size="small"
                                    color="inherit"
                                    sx={{ ml: "5px", backgroundColor: '#454545', width: "128px", height: "50px", color: 'white', '&:hover': { backgroundColor: '#454545' } }}
                                    startIcon={<TuneIcon />}
                                    onClick={() => setDrawerExpanded(true)}
                                >
                                    All Filters
                                </Button>
                            </Tooltip>

                            {/* Date Filters + Search Button Container */}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "15px",
                                    flex: 1, // take remaining space
                                    alignItems: "center",
                                }}
                            >
                                {/* From Date */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: { xs: "100%", sm: "250px" },
                                        gap: "10px",
                                    }}
                                >
                                    <Typography>From</Typography>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            renderInput={(props) => (
                                                <TextField
                                                    {...props}
                                                    fullWidth
                                                    size="small"
                                                    inputProps={{ ...props.inputProps, readOnly: true }}
                                                    sx={{ height: "2.45rem" }}
                                                />
                                            )}
                                            value={fromDate}
                                            inputFormat="DD/MM/YYYY"
                                            onChange={(newValue) => {
                                                const now = dayjs();
                                                const fromDate = dayjs(newValue)
                                                    .hour(now.hour())
                                                    .minute(now.minute())
                                                    .second(now.second())
                                                    .millisecond(now.millisecond())
                                                    .toDate();
                                                setFromDate(fromDate);
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Box>

                                {/* To Date */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: { xs: "100%", sm: "250px" },
                                        gap: "10px",
                                    }}
                                >
                                    <Typography>To</Typography>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            renderInput={(props) => (
                                                <TextField
                                                    {...props}
                                                    fullWidth
                                                    size="small"
                                                    inputProps={{ ...props.inputProps, readOnly: true }}
                                                    sx={{ height: "2.45rem" }}
                                                />
                                            )}
                                            value={toDate}
                                            inputFormat="DD/MM/YYYY"
                                            onChange={(newValue) => {
                                                const now = dayjs();
                                                const toDate = dayjs(newValue)
                                                    .hour(now.hour())
                                                    .minute(now.minute())
                                                    .second(now.second())
                                                    .millisecond(now.millisecond())
                                                    .toDate();
                                                setToDate(toDate);
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Box>

                                {/* Search Button */}
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => handleSearch(stoneList)}
                                    startIcon={<SearchOutlinedIcon />}
                                    sx={{
                                        height: "40px",
                                        ml: { xs: 0, sm: 2 },
                                        width: { xs: "100%", sm: "auto" },
                                    }}
                                >
                                    Search
                                </Button>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .lavenderCell": {
                                    color: "#080808",
                                    backgroundColor: "#D8BFD8",
                                },
                                "& .greenCell": {
                                    color: "#080808",
                                    backgroundColor: "#B6D7A8"
                                },
                                "& .redCell": {
                                    color: "#080808",
                                    backgroundColor: "#f5d7d7",
                                },
                                "& .yellowCell": {
                                    color: "#080808",
                                    backgroundColor: "#F1E68C",
                                },
                                height: "75vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={false}
                                density="compact"
                                loading={stoneListLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                slots={{ toolbar: CustomToolbar }}
                                rows={stonePotentialList}
                                columns={columns}
                                getRowId={(row) => row.stoneid}
                                rowSpacingType="border"
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns status and traderName, the other columns will remain visible
                                            id: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                )
            }
        </Box>
    )
}

export default StonePotential;